<template>
  <pf-card>
    <pf-card-header>
      <pf-card-title>
        <pf-title h="2"><server-icon /> {{ server.nome }}</pf-title>
      </pf-card-title>

      <template #actions>
        <pf-dropdown v-model:open="menuOpen" position="right" v-if="server.has_cockpit || server.cpanel || server.has_console || showPowerActions">
          <template #toggle>
            <pf-menu-toggle variant="plain" />
          </template>

          <pf-dropdown-item
            v-if="server.cpanel"
            :disabled="server.is_under_maintenance"
            :component="linkComponent"
            :to="whmUrl"
            target="_blank"
          >
            <template #icon><arrow-up-right-from-square-icon /></template>
            {{ $t('Accedi a WHM') }}
          </pf-dropdown-item>

          <pf-dropdown-item
            v-if="server.has_cockpit"
            :disabled="server.is_under_maintenance"
            :component="linkComponent"
            :to="cockpitUrl"
            target="_blank"
          >
            <template #icon><arrow-up-right-from-square-icon /></template>
            {{ $t('Pannello di controllo Cockpit') }}
          </pf-dropdown-item>

          <pf-dropdown-item
            v-if="server.has_console"
            :disabled="server.is_under_maintenance"
            :component="linkComponent"
            :to="`/console/?id=${server.id}/`"
            target="popup"
            @click.prevent="popup(`/console/?id=${server.id}/`)"
          >
            <template #icon><terminal-icon /></template>
            {{ $t('Apri console') }}
          </pf-dropdown-item>

          <pf-dropdown-item v-if="server.has_cockpit" :disabled="server.is_under_maintenance" @click="backupAccessServer = server.nome">
            <template #icon><cloud-arrow-up-icon /></template>
            {{ $t('Richiedi accesso ai backup') }}
          </pf-dropdown-item>

          <template v-if="showPowerActions">
            <pf-divider />

            <template v-if="server.is_under_maintenance">
              <pf-dropdown-item disabled>
                <template #icon><play-icon /></template>
                {{ $t('Accendi') }}
              </pf-dropdown-item>

              <pf-dropdown-item disabled>
                <template #icon><power-off-icon /></template>
                {{ $t('Spegni') }}
              </pf-dropdown-item>

              <pf-dropdown-item disabled>
                <template #icon><rotate-right-icon /></template>
                {{ $t('Riavvia') }}
              </pf-dropdown-item>
            </template>

            <pf-dropdown-item v-else-if="powerStateStatus.updating" disabled>
              <template #icon><pf-spinner size="md" /></template>
              {{ $t('Verifica dello stato del server…') }}
            </pf-dropdown-item>

            <pf-dropdown-item v-else-if="powerStateStatus.state === AsyncComputedState.Error" disabled>
              <template #icon><rotate-right-icon /></template>
              {{ $t('Impossibile verificare lo stato del server') }}
            </pf-dropdown-item>

            <pf-dropdown-item v-else-if="powerState === 'off'" @click="power('start')">
              <template #icon><play-icon /></template>
              {{ $t('Accendi') }}
            </pf-dropdown-item>

            <template v-else-if="powerState === 'on'">
              <pf-dropdown-item @click="power('stop')">
                <template #icon><power-off-icon /></template>
                {{ $t('Spegni') }}
              </pf-dropdown-item>

              <pf-dropdown-item @click="power('restart')">
                <template #icon><rotate-right-icon /></template>
                {{ $t('Riavvia') }}
              </pf-dropdown-item>
            </template>
          </template>
        </pf-dropdown>
      </template>
    </pf-card-header>

    <pf-card-body>
      <pf-alert v-if="server.is_under_maintenance" variant="warning" inline :title="$t('Manutenzione in corso')" class="pf-v6-u-mb-md" />

      <pf-description-list>
        <pf-description-list-group v-if="server.feature">
          <pf-description-list-description>
            <pf-flex>
              <pf-flex-item>{{ server.feature.core }} <cpu-icon title="cpu" /></pf-flex-item>
              <pf-flex-item>{{ server.feature.ram }} GB <memory-icon title="ram" /></pf-flex-item>
              <pf-flex-item>{{ formatSize(server.feature.spazio * 1000000) }} <volume-icon title="storage" /></pf-flex-item>
            </pf-flex>
          </pf-description-list-description>
        </pf-description-list-group>

        <pf-description-list-group v-if="server.stack">
          <pf-description-list-term>{{ $t('Stack') }}</pf-description-list-term>
          <pf-description-list-description>
            {{ server.stack }}
          </pf-description-list-description>
        </pf-description-list-group>

        <pf-description-list-group v-if="server.fqdn">
          <pf-description-list-term>{{ $t('Dominio') }}</pf-description-list-term>
          <pf-description-list-description>
            {{ server.fqdn }}
          </pf-description-list-description>
        </pf-description-list-group>

        <pf-description-list-group v-if="server.ip4_public">
          <pf-description-list-term>{{ $t('Indirizzo IP') }}</pf-description-list-term>
          <pf-description-list-description>
            {{ server.ip4_public }}
          </pf-description-list-description>
        </pf-description-list-group>

        <pf-description-list-group v-if="server.os">
          <pf-description-list-term>{{ $t('Sistema operativo') }}</pf-description-list-term>
          <pf-description-list-description> {{ server.os }} {{ server.osrelease }} </pf-description-list-description>
        </pf-description-list-group>

        <pf-flex v-if="server.server_location">
          <pf-flex-item>
            <pf-description-list-group>
              <pf-description-list-term>{{ $t('Server location') }}</pf-description-list-term>
              <pf-description-list-description>
                {{ server.server_location }}
              </pf-description-list-description>
            </pf-description-list-group>
          </pf-flex-item>
          <pf-flex-item>
            <pf-description-list-group v-if="server.backup_location">
              <pf-description-list-term>{{ $t('Backup Location') }}</pf-description-list-term>
              <pf-description-list-description>
                {{ server.backup_location }}
              </pf-description-list-description>
              <pf-description-list-description>
                <pf-content component="p" class="pf-v6-u-font-size-xs">{{ allFeatures.backup.tooltip }}</pf-content>
              </pf-description-list-description>
            </pf-description-list-group>
          </pf-flex-item>
        </pf-flex>
      </pf-description-list>
    </pf-card-body>
  </pf-card>

  <x-form
    v-if="backupAccessServer"
    modal
    open
    :title="$t('Richiesta di accesso ai backup del server {0}', [backupAccessServer])"
    variant="medium"
    @submit="backupAccess"
    @close="backupAccessServer = null"
  >
    <form-group name="Email" required>
      <x-input type="email" v-model="backupAccessEmail" name="email" required />
    </form-group>
  </x-form>
</template>

<script lang="ts" setup>
import { type Ref, computed, ref } from 'vue';
import { AsyncComputedState, asyncComputed } from '@common/asyncComputed';
import { $t as _t } from '@/i18n';
import type { Server, ServerPowerAction } from '@/resources';
import { getAllProductFeatures } from './product-feature.vue';
import ArrowUpRightFromSquareIcon from '@vue-patternfly/icons/arrow-up-right-from-square-icon';
import ServerIcon from '@vue-patternfly/icons/server-alt-icon';
import TerminalIcon from '@vue-patternfly/icons/terminal-icon';
import PlayIcon from '@vue-patternfly/icons/play-icon';
import PowerOffIcon from '@vue-patternfly/icons/power-off-icon';
import RotateRightIcon from '@vue-patternfly/icons/rotate-right-icon';
import CpuIcon from '@vue-patternfly/icons/cpu-icon';
import MemoryIcon from '@vue-patternfly/icons/memory-icon';
import VolumeIcon from '@vue-patternfly/icons/volume-icon';
import XInput from '@common/XInput.vue';
import CloudArrowUpIcon from '@vue-patternfly/icons/cloud-arrow-up-icon';
import { ServerPowerStateResource, TicketResource } from '@/resources';
import { useLoginStore } from '@/store/login';
import { useAlertStore } from '@/store/alerts';
import { formatSize } from '@common/utils';

const props = defineProps<{
  server: Server;
}>();

const login = useLoginStore();
const alertStore = useAlertStore();
const allFeatures = getAllProductFeatures();

const backupAccessServer: Ref<string | null> = ref(null);
const backupAccessEmail = ref(login.email);

const menuOpen = ref(false);
// Con component="a" il PfDropdownItem disabilitato mantiene href: in manutenzione serve un vero <button disabled>
const linkComponent = computed(() => (props.server.is_under_maintenance ? 'button' : 'a'));
const showPowerActions = computed(() => props.server.is_zenith && props.server.allow_user_power_cycle);
const { result: powerState, state: powerStateStatus } = asyncComputed(
  async () => {
    if (!menuOpen.value || !showPowerActions.value || props.server.is_under_maintenance) {
      return null;
    }
    return (await new ServerPowerStateResource().get(props.server.id)).state;
  },
  null,
  menuOpen,
);

function popup(url: string, options = 'width=1250,height=1000') {
  window.open(url, 'popup', options);
}

async function power(action: ServerPowerAction) {
  const confirmMessages: Partial<Record<ServerPowerAction, string>> = {
    stop: _t('Vuoi davvero spegnere il server {0}?', [props.server.nome]),
    restart: _t('Vuoi davvero riavviare il server {0}?', [props.server.nome]),
  };

  const confirmMessage = confirmMessages[action];
  if (confirmMessage && !window.confirm(confirmMessage)) {
    return;
  }

  await new ServerPowerStateResource().put(props.server.id, { action });
}

async function backupAccess() {
  if (!backupAccessServer || !backupAccessEmail) {
    return;
  }

  await new TicketResource().post({
    titolo: `Richiesta di accesso ai backup del server ${backupAccessServer.value} da parte di ${backupAccessEmail.value}`,
  });

  alertStore.add({ title: _t('Richiesta inviata, riceverai risposta al più presto'), type: 'success', toast: true });
  backupAccessServer.value = null;
}

// URL fuori dal template: vue-tsc 3.3.13 interpreta il // delle URL nelle prop come commento (vuejs/language-tools#6240)
const whmUrl = computed(() => `https://${props.server.fqdn}:2087`);
const cockpitUrl = computed(() => `https://${props.server.fqdn}/cockpit`);
</script>
